import {Component, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DialogService, NotificationService, PrefabService} from "@wms/services";
import {
  BaseComponent,
  WmsDialogBodyComponent, WmsDialogComponent,
  WmsDialogFooterComponent,
  WmsDialogHeaderComponent
} from "@wms/components";
import { PrefabPublishService } from "@wms/prefab-publish";

import {WmsWizardComponent} from 'src/app/components/wms-wizard/wms-wizard.component';
import {ConfigurationComponent} from "./screens/configuration/configuration.component";
import {SummaryComponent} from "@wms/app/prefab-publish/publish-to-team/screens/summary/summary.component";
import {InformationComponent} from "@wms/app/prefab-publish/publish-to-team/screens/information/information.component";
import {
  DocumentationComponent
} from "@wms/app/prefab-publish/publish-to-team/screens/documentation/documentation.component";
import {camelCase, get, includes, isObject, upperFirst, uniqBy} from "lodash-es";
import {getPrefabIconUrl, replace} from "@wms/core";

const semver = require('semver');

@Component({
  selector    : 'wms-publish-to-team',
  standalone  : true,
  imports: [
    CommonModule,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    ConfigurationComponent,
    WmsDialogComponent,
    WmsWizardComponent,
    SummaryComponent,
    InformationComponent,
    DocumentationComponent
  ],
  templateUrl : './publish-to-team.component.html',
  styleUrls   : ['./publish-to-team.component.less']
})
export class PublishToTeamComponent extends BaseComponent {
  stepCounter: number;
  currentStep: any;
  projectDetails: any;
  nextBtnText: string;
  steps: any[];
  artifactType: string;
  projectObj: any;

  categories: any[];
  currentVersion: string;
  suggestedVersions: any[];
  dependencies: any[];
  processing: boolean;
  artifactPublishDetails: any;
  artifactDetails: any;
  isNewArtifact: boolean;
  draftAlert: boolean;
  isPublishSuccessful: boolean;
  documentationUrl: string;
  showErrMsg: boolean;
  errMsg: any;
  successMessage: string;
  validatingVersion: boolean;

  @ViewChild('configurationScreen') configurationScreen : TemplateRef<any>;
  @ViewChild('documentationScreen') documentationScreen : TemplateRef<any>;
  @ViewChild('informationScreen') informationScreen : TemplateRef<any>;
  @ViewChild('summaryScreen') summaryScreen : TemplateRef<any>;

  constructor(private prefabPublishService: PrefabPublishService, private prefabService: PrefabService,
              private dialogService: DialogService, private notificationService: NotificationService) {
    super();
    this.eventManager.publishToTeamInitObservable().subscribe(() => this.init());
  }

  init() {
    this.projectDetails = this.projectService.getDetails();
    this.currentStep    = undefined;
    this.stepCounter    = -1;
    this.steps          = [
      {
        "id"          : "information",
        "label"       : this.locale.LABEL_ARTIFACT_PUBLISH_INFO,
        "description" : this.locale.LABEL_ARTIFACT_PUBLISH_INFO_DESCRIPTION.replace("${0}", this.projectDetails.displayName),
        "stepTemplate": this.informationScreen
      },
      {
        "id"          : "configuration",
        "label"       : this.locale.LABEL_CONFIG,
        "description" : this.locale.LABEL_CONFIG_DESCRIPTION.replace("${0}", 'PREFAB'),
        "stepTemplate": this.configurationScreen
      },
      {
        "id"          : "documentation",
        "label"       : this.locale.USER_GUIDE,
        "description" : this.locale.LABEL_DOCUMENTATION_DESCRIPTION.replace("${0}", 'PREFAB'),
        "stepTemplate": this.documentationScreen
      },
      {
        "id"          : "summary",
        "label"       : this.locale.LABEL_ARTIFACT_SUMMARY,
        "description" : this.locale.LABEL_ARTIFACT_SUMMARY_DESCRIPTION,
        "stepTemplate": this.summaryScreen
      }
    ];
    this.artifactPublishDetails = {};
    this.categories             = [];
    this.suggestedVersions      = [];
    this.dependencies           = [];
    this.processing             = false;
    this.artifactDetails        = {};
    this.projectObj             = {};
    this.isNewArtifact          = false;
    this.draftAlert             = false;
    this.isPublishSuccessful    = false;
    this.artifactType           = "PREFAB";
    this.documentationUrl       = this.prefabPublishService.ARTIFACTS_PUBLISH_DOC_API_URL.replace(/\:projectID/ig, this.projectDetails.id);
    this.prefabPublishService.getArtifactCategories(this.categories, this.processing, this.artifactPublishDetails).then(() => this.getArtifactDetails());
  }

  closeModal() {
    this.dialogService.close('publish-to-team');
  }

  incrementStep() {
    if (this.isFinalStep()) {
      this.publishToTeam();
    } else {
      this.stepCounter += 1;
      this.onStepChange();
    }
  }

  decrementStep() {
    this.stepCounter -= 1;
    this.onStepChange();
  }


  onStepChange() {
    this.currentStep = this.steps[this.stepCounter];
    this.nextBtnText = this.isFinalStep() ? this.locale.LABEL_TEMPLATE_PROPS_PUBLISH : this.locale.LABEL_NEXT;
  }

  isFinalStep() {
    return this.stepCounter && this.stepCounter === (this.steps.length - 1);
  }

  getArtifactDetails() {
    if(!this.isNewArtifact && !Object.keys(this.artifactDetails).length) {
      this.processing = true;
      let urlParams = {
        artifactName  : this.projectService.projectEdnDetails.name,
        artifactType  : this.artifactType,
        branch        : this.projectService.projectEdnDetails.vcsBranch
      }
      this.projectService.getArtifact(urlParams).then((artifactDetails: any) => {
        this.artifactDetails = artifactDetails;
        this.setArtifactPublishDetails();
        this.processing = false;
      }, (error: any) => {
        let errorObj = error.error.errors.error[0];
        this.processing = false;
        //if the user is publishing it for the first time, it will throw 500.
        if (error && error.status === 500 && errorObj.messageKey === "ARTIFACT_NOT_FOUND_BY_PROJECT_ID_AND_TYPE") {
          this.isNewArtifact = true;
          this.setArtifactPublishDetails();
        } else if (error && error.status === 500 && errorObj.messageKey === "ARTIFACT_ALREADY_IN_DRAFT_STATE") {
          this.draftAlert = true;
        } else {
          this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, errorObj.message);
        }
      });
    }
  }

  addToSuggestedVers(version: string) {
    this.suggestedVersions.push({
      version: version,
      category: this.locale.LABEL_SUGGESTED_VERSIONS
    });
  }

  populateSuggestedVersions(currentVersion: string) {
    if (get(this.artifactDetails, 'artifactVO.status') === 'DRAFT') {
      this.addToSuggestedVers(currentVersion);
    } else {
      this.addToSuggestedVers(semver.inc(currentVersion, 'patch'));
      this.addToSuggestedVers(semver.inc(currentVersion, 'minor'));
      this.addToSuggestedVers(semver.inc(currentVersion, 'major'));
      this.suggestedVersions = uniqBy(this.suggestedVersions, 'version');
    }
  }
  setArtifactPublishDetails() {
    this.setProjectObj();
    let artifactVersion   = get(this.artifactDetails, 'artifactVO.version', '0.0.0'),
        artifactCategory  = get(this.artifactDetails, 'artifactVO.category', "");
    let artifactVersionParts = artifactVersion.split(".");
    if (!artifactVersionParts[2]) {
      artifactVersionParts[2] = '0';
    };
    this.currentVersion = artifactVersionParts.join('.');
    this.populateSuggestedVersions(this.currentVersion);
    this.artifactPublishDetails = {
      "isNew"              : this.isNewArtifact,
      "sourceProjectId"    : this.projectObj.sourceProjectId,
      "artifactType"       : this.artifactType,
      "displayName"        : this.projectObj.displayName,
      'name'               : this.projectObj.name,
      'categories'         : get(this.projectObj, 'categories', []),
      'icon'               : '',
      'description'        : get(this.projectObj, 'description', ""),
      'isValid'            : true,
      'tags'               : get(this.artifactDetails, 'artifactVO.tags', []) === null ? [] : get(this.artifactDetails, 'artifactVO.tags', []),
      'dependencies'       : get(this.artifactDetails, 'dependencies', []),
      'metadataObjs'       : get(this.artifactDetails, 'artifactMetadata', []),
      'isDraft'            : get(this.artifactDetails, 'artifactVO.status') === 'DRAFT',
      'category'           : includes(this.categories, artifactCategory) ? artifactCategory : "",
      'query'              : artifactCategory || "",
      'newCategory'        : "",
      'commitMsg'          : this.isNewArtifact ? "" : (get(this.artifactDetails, 'artifactVO.status', 'PUBLISHED') === "DRAFT" ? get(this.artifactDetails, 'artifactVO.publisherComments', "") : ""),
      'version'            : artifactVersion,
      'currentVersion'     : this.currentVersion,
      'newVersion'         : get(this.artifactDetails, 'artifactVO.status') === 'DRAFT' ? this.suggestedVersions[0]?.version : "",
      'suggestedVersions'  : this.suggestedVersions,
    };
    this.setPrefabIcon();
  }

  setProjectObj() {
    this.projectObj = {
      "sourceProjectId"  : this.isNewArtifact ? this.projectService.projectEdnDetails.projectId : this.artifactDetails.artifactVO.sourceProjectId,
      'name'             : this.isNewArtifact ? this.projectDetails.name : this.artifactDetails.artifactVO.name,
      'displayName'      : this.isNewArtifact ? this.projectDetails.displayName : this.artifactDetails.artifactVO.displayName,
      'artifactType'     : this.isNewArtifact ? this.artifactType : this.artifactDetails.artifactVO.artifactType,
      'description'      : this.projectDetails.description, // Fix for WMS-25931: Always use the project description for description field in publish prefab dialog
      'icon'             : this.projectDetails.icon,
      'categories'       : this.categories
    };
  }

  setPrefabIcon() {
    this.prefabService.getConfig().then((config: any) => {
      this.artifactPublishDetails.icon = getPrefabIconUrl(config.iconUrl, this.projectService.getProjectId());
      this.setDependencies();
    })
  }

  setDependencies() {
    if(!this.dependencies.length) {
      this.processing = true;
      this.projectService.getDependencies(this.artifactType).then((dependencies: any) => {
        this.dependencies = dependencies;
        this.processing   = false;
        this.incrementStep();
      }, (error: any) => {
        this.processing = false;
        this.notificationService.show('error', this.locale.MESSAGE_ERROR_TITLE, error);
      });
    }
  }

  isValid(): boolean {
    if (this.currentStep?.id === 'information' && this.artifactPublishDetails) {
      return !this.artifactPublishDetails.isValid || !this.artifactPublishDetails.commitMsg || !this.artifactPublishDetails.category || !this.artifactPublishDetails.query || !this.artifactPublishDetails.newVersion
    }
    return false;
  }

  publishToTeam() {
    this.processing = true;
    this.showErrMsg = false;
    let payload: any = {
      "sourceProjectId"    : this.projectService.projectEdnDetails.projectId,
      "name"               : this.artifactPublishDetails.name,
      "version"            : this.artifactPublishDetails.newVersion,
      "category"           : this.artifactPublishDetails.category,
      "description"        : this.artifactPublishDetails.description,
      "publisherComments"  : this.artifactPublishDetails.commitMsg,
      "tags"               : this.artifactPublishDetails.tags,
      "artifactType"       : this.artifactPublishDetails.artifactType,
      "displayName"        : this.artifactPublishDetails.displayName,
      "dependencies"       : this.dependencies,
      "artifactMetadataList": []
    };

    this.projectService.publishToTeam(payload).then((response: any) => {
      this.successMessage                 = this.locale.LABEL_ARTIFACT_SUCCESS_MESSAGE_TEAM.replace("${0}", upperFirst(camelCase(this.artifactPublishDetails.artifactType)));
      this.isPublishSuccessful            = true;
      this.artifactPublishDetails.isValid = false;
      this.processing                     = false;
    }, (error: any) => {
      this.showErrMsg = true;
      this.errMsg     = {
        type: 'error',
        caption: this.publishErrorHandler(error)
      }
      this.processing = false;
    });
  }

  publishErrorHandler(errMsg: string) {
    const parameters: string | undefined = get(errMsg, 'error.errors.error[0].parameters[0]');
    return !isObject(errMsg) ? errMsg : replace(get(errMsg, 'error.errors.error[0].message'), parameters !== undefined ? (parameters as string).split(',') : undefined, true)
  }
}
